import { BookOpen, ClipboardList, FormInput, GitFork, History, Printer, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MindMap, MindNode } from "../components/MindMap";
import { Button, Input, PageHeader } from "../components/ui";
import { api, downloadFile, errMsg } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { cn } from "../lib/utils";

type Section = { id: string; title: string; items: string[] };
type Sop = { id: string; title: string; role: string; steps: string[]; pdf?: string };
type Form = { id: string; title: string; fields: [string, string][] };
type Guide = {
  sections: Section[]; sop: Sop[]; forms: Form[]; mindmap: MindNode;
  changelog: { date: string; version: string; items: string[] }[];
  meta: { version: string; released: string; guide_updated: string; revisions: number };
};

const TABS = [
  { id: "panduan", label: "Panduan", icon: BookOpen },
  { id: "sop", label: "SOP", icon: ClipboardList },
  { id: "form", label: "Cara Isi Form", icon: FormInput },
  { id: "mindmap", label: "Mind Map", icon: GitFork },
  { id: "revisi", label: "Riwayat Revisi", icon: History },
];

function Mark({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="bg-amber-200 dark:bg-amber-500/40 rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

const has = (q: string, ...s: string[]) => !q || s.some((x) => x.toLowerCase().includes(q.toLowerCase()));

export default function Guide() {
  const [g, setG] = useState<Guide | null>(null);
  const [tab, setTab] = useState("panduan");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/guide").then((r) => setG(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const filtered = useMemo(() => {
    if (!g) return null;
    const sections = g.sections.map((s) => ({ ...s, items: s.items.filter((t) => has(q, t, s.title)) })).filter((s) => s.items.length);
    const sop = g.sop.map((s) => ({ ...s, steps: s.steps.map((t, idx) => ({ t, idx })).filter((x) => has(q, s.title, s.role) || has(q, x.t)) })).filter((s) => s.steps.length);
    const forms = g.forms.map((f) => ({ ...f, fields: has(q, f.title) ? f.fields : f.fields.filter(([n, h]) => has(q, n, h)) })).filter((f) => f.fields.length);
    const changelog = g.changelog.filter((c) => has(q, c.version, ...c.items));
    const mm = !q || JSON.stringify(g.mindmap).toLowerCase().includes(q.toLowerCase());
    return { sections, sop, forms, changelog, counts: { panduan: sections.reduce((a, s) => a + s.items.length, 0), sop: sop.reduce((a, s) => a + s.steps.length, 0), form: forms.reduce((a, f) => a + f.fields.length, 0), mindmap: mm ? 1 : 0, revisi: changelog.length } };
  }, [g, q]);

  if (!g || !filtered) return <div className="text-sm text-muted-foreground">Memuat panduan…</div>;
  const total = Object.values(filtered.counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="guide-page">
      <PageHeader
        title="Panduan Sistem"
        subtitle="Dokumentasi, SOP, cara isi form, dan peta pikiran Yutaka Edu-Finance · otomatis terbarui setiap revisi"
        actions={<span className="rounded-full border bg-card px-3 py-1 text-xs font-semibold num" data-testid="guide-version-badge">Versi {g.meta.version} · {g.meta.revisions} revisi · diperbarui {fmtDateTime(g.meta.guide_updated)}</span>}
      />
      <div className="card-surface p-3 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="guide-search-input" className="pl-9 pr-9" placeholder="Cari kata kunci: kwitansi, NIK, rollover, void, tutup kas…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" data-testid="guide-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted" onClick={() => setQ("")}><X className="h-4 w-4" /></button>}
        </div>
        {q && <span className="text-xs text-muted-foreground num" data-testid="guide-search-count">{total} hasil untuk "{q}"</span>}
      </div>
      <div className="flex flex-wrap gap-1 border-b mb-5" data-testid="guide-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" data-testid={`guide-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={cn("flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors", tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}>
            <t.icon className="h-4 w-4" /> {t.label}
            {q && <span className={cn("rounded-full px-1.5 text-[10px] num", filtered.counts[t.id as keyof typeof filtered.counts] ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>{filtered.counts[t.id as keyof typeof filtered.counts]}</span>}
          </button>
        ))}
      </div>

      {tab === "panduan" && (
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          <nav className="hidden lg:block sticky top-20 self-start space-y-1 text-sm">
            {filtered.sections.map((s) => <a key={s.id} href={`#${s.id}`} className="block rounded px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`guide-nav-${s.id}`}>{s.title}</a>)}
          </nav>
          <div className="space-y-4">
            {filtered.sections.length === 0 && <p className="text-sm text-muted-foreground" data-testid="guide-empty">Tidak ada hasil.</p>}
            {filtered.sections.map((s, i) => (
              <section key={s.id} id={s.id} className="card-surface rise p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`guide-section-${s.id}`}>
                <h2 className="text-lg font-semibold mb-3"><Mark text={s.title} q={q} /></h2>
                <ul className="space-y-2 text-sm leading-relaxed">
                  {s.items.map((t, j) => <li key={j} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span><Mark text={t} q={q} /></span></li>)}
                </ul>
              </section>
            ))}
          </div>
        </div>
      )}

      {tab === "sop" && (
        <div className="space-y-4">
          {filtered.sop.length === 0 && <p className="text-sm text-muted-foreground" data-testid="guide-empty">Tidak ada hasil.</p>}
          {filtered.sop.map((s, i) => (
            <section key={s.id} className="card-surface rise p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`guide-sop-${s.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h2 className="text-lg font-semibold"><Mark text={s.title} q={q} /></h2>
                <span className="rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">Pelaksana: <Mark text={s.role} q={q} /></span>
                {s.pdf && <Button size="sm" data-testid={`guide-sop-pdf-${s.id}`} onClick={() => downloadFile(s.pdf!, "panduan_instal_aplikasi_siswa.pdf", true).catch((e) => toast.error(errMsg(e)))}><Printer className="h-4 w-4" /> Cetak SOP (PDF)</Button>}
              </div>
              <ol className="space-y-2.5 text-sm leading-relaxed">
                {s.steps.map(({ t, idx }) => (
                  <li key={idx} className="flex gap-3">
                    <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[hsl(var(--sidebar))] text-white text-xs font-bold num">{idx + 1}</span>
                    <span className="pt-0.5"><Mark text={t} q={q} /></span>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}

      {tab === "form" && (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.forms.length === 0 && <p className="text-sm text-muted-foreground" data-testid="guide-empty">Tidak ada hasil.</p>}
          {filtered.forms.map((f, i) => (
            <section key={f.id} className="card-surface rise p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`guide-form-${f.id}`}>
              <h2 className="text-base font-semibold mb-3"><Mark text={f.title} q={q} /></h2>
              <dl className="divide-y text-sm">
                {f.fields.map(([n, h]) => (
                  <div key={n} className="py-2 grid grid-cols-[minmax(120px,40%)_1fr] gap-3">
                    <dt className="font-semibold text-foreground"><Mark text={n} q={q} /></dt>
                    <dd className="text-muted-foreground leading-relaxed"><Mark text={h} q={q} /></dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      )}

      {tab === "mindmap" && (
        <section className="card-surface p-5" data-testid="guide-mindmap">
          <h2 className="text-lg font-semibold mb-1">Peta Pikiran Aplikasi</h2>
          <p className="text-sm text-muted-foreground mb-4">Enam cabang utama dan fungsi di bawahnya. Kata kunci pencarian akan disorot pada cabang yang cocok.</p>
          <MindMap root={g.mindmap} highlight={q} />
        </section>
      )}

      {tab === "revisi" && (
        <section className="card-surface p-5" data-testid="guide-section-revisi">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Riwayat Revisi</h2>
          {filtered.changelog.length === 0 && <p className="text-sm text-muted-foreground" data-testid="guide-empty">Tidak ada hasil.</p>}
          <ol className="relative border-l pl-5 space-y-5">
            {[...filtered.changelog].reverse().map((c) => (
              <li key={c.version} className="relative" data-testid={`guide-changelog-${c.version}`}>
                <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                <p className="text-sm font-semibold">Versi {c.version} <span className="text-xs text-muted-foreground font-normal num">· {fmtDate(c.date)}</span></p>
                <ul className="mt-1 space-y-1 text-sm text-muted-foreground list-disc pl-4">{c.items.map((t, j) => <li key={j}><Mark text={t} q={q} /></li>)}</ul>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
